import React, { ChangeEvent, useEffect, useMemo, useState } from "react";
import {
  Checkbox,
  Chip,
  FilledInputProps,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  InputAdornment,
  InputLabel,
  MenuItem,
  MenuProps,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  SxProps,
  TextField,
  Theme,
  Box,
} from "@mui/material";
import { Checkmark } from "components/Icons";
import { distinct } from "./coll";
import PSelect from "components/PSelect";
import { CustomCaseProperty, CustomDocumentProperty, CustomPropertyType } from "generated/graphql";
import { CheckboxListItem, UseForm } from "./FormHelpers";
import SelectOptions from "components/SelectOptions/SelectOptions";

export function FormCustomProperties<Field extends string>(props: {
  id: Field;
  sx?: SxProps<Theme>;
  useForm: UseForm<Field>;
  customProperties: (CustomCaseProperty | CustomDocumentProperty)[];
}): React.ReactElement {
  const { id, sx, useForm, customProperties } = props;
  const keyValues = useForm.formStateHandle[0].values[id] || [];
  const keyValuesMap = useMemo(() => {
    return customProperties
      .map((cp) => cp.key)
      .reduce((prev, cpKey) => {
        const property = customProperties.find((cp) => cp.key === cpKey);

        if (property) {
          prev[cpKey] = keyValues.find((kv) => kv.key === cpKey)?.values || [];
        }

        return prev;
      }, {});
  }, [customProperties, keyValues]);

  const handleValueChange = (key: string, values: string[]) => {
    const mutableKeyValues = keyValues.slice();
    const keyValueIndex = mutableKeyValues.findIndex((kv) => kv.key === key);

    if (values.length) {
      if (keyValueIndex > -1) {
        const newKeyValue = {
          key,
          values,
        };
        mutableKeyValues.splice(keyValueIndex, 1, newKeyValue);
        useForm.updateField(id)(mutableKeyValues);
      } else {
        useForm.updateField(id)([
          ...keyValues,
          {
            key,
            values,
          },
        ]);
      }
    } else {
      if (keyValueIndex > -1) {
        mutableKeyValues.splice(keyValueIndex, 1);
        useForm.updateField(id)(mutableKeyValues);
      }
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, ...sx }}>
      {customProperties.map((cp) => (
        <Box key={cp.key}>
          {cp.dataType === CustomPropertyType.Select ? (
            <FormControl fullWidth>
              <InputLabel>{cp.key}</InputLabel>
              <PSelect
                multiple
                label={cp.key}
                value={keyValuesMap[cp.key]}
                onChange={(e) => handleValueChange(cp.key, e.target.value as string[])}>
                {cp.selectOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </PSelect>
            </FormControl>
          ) : (
            <SelectOptions
              label={cp.key}
              options={keyValuesMap[cp.key]}
              setOptions={(values) => {
                const distinctValues = distinct(values);
                const canUpdate =
                  cp.dataType === CustomPropertyType.Number ? distinctValues.every((v) => !isNaN(parseInt(v))) : true;
                if (canUpdate) {
                  handleValueChange(cp.key, distinctValues);
                }
              }}
            />
          )}
        </Box>
      ))}
    </Box>
  );
}

export function FormMultiSelectField<Field extends string>(props: {
  label: string;
  id: Field;
  fullWidth?: boolean;
  required?: boolean;
  useForm: UseForm<Field>;
  options: string[];
  disabled?: boolean;
  MenuProps?: MenuProps;
  onChange?: (values: string[]) => void;
}): React.ReactElement {
  const { id, useForm, label, options, onChange, disabled } = props;
  const [state, setState] = useState<string[]>(useForm.formStateHandle[0].values[id] || []);
  const handleChange = (event: SelectChangeEvent<typeof state>) => {
    const {
      target: { value },
    } = event;
    setState(
      // On autofill we get a stringified value.
      typeof value === "string" ? value.split(",") : value
    );
  };

  useEffect(() => {
    useForm.updateField(id)(state);
    if (onChange) onChange(state);
  }, [state]);

  return (
    <FormControl fullWidth>
      <InputLabel id={`${id}-multi-select`}>{label}</InputLabel>
      <Select
        labelId={`${id}-multi-select`}
        multiple
        disabled={disabled}
        value={state}
        onChange={handleChange}
        input={<OutlinedInput label={label} />}
        renderValue={(selected) => (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {selected.map((value) => (
              <Chip key={value} label={value} />
            ))}
          </Box>
        )}
        MenuProps={props.MenuProps || {}}>
        {options.map((option) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export function FormSelectField<Field extends string, V extends string | number>(props: {
  className?: string;
  margin?: "dense" | "none";
  label: string;
  id: Field;
  fullWidth?: boolean;
  required?: boolean;
  useForm: UseForm<Field>;
  options: V[];
  disabled?: boolean;
  variant?: "standard" | "outlined" | "filled";
  optionDisplay?: (option: V) => string;
}): React.ReactElement {
  const { id, useForm } = props;
  return (
    <FormControl margin="none" size="small" fullWidth={props.fullWidth}>
      <InputLabel variant="outlined" style={{ backgroundColor: "white" }}>
        {props.label}
      </InputLabel>
      <PSelect
        id={id}
        label={props.label}
        style={{ minWidth: "100px" }}
        name={id}
        margin={props.margin}
        variant={props.variant || "outlined"}
        color="primary"
        required={props.required}
        className={props.className}
        fullWidth={props.fullWidth}
        error={useForm.hasError(id)}
        onChange={(evt) => useForm.updateField(id)(evt.target.value || undefined)}
        value={useForm.formStateHandle[0].values[id] || ""}
        disabled={props.disabled}>
        {!props.required && <MenuItem value="">None</MenuItem>}
        {props.options.map((opt) => (
          <MenuItem key={opt} value={opt}>
            {props.optionDisplay ? props.optionDisplay(opt) : opt}
          </MenuItem>
        ))}
      </PSelect>
    </FormControl>
  );
}

export function FormCheckbox<Field extends string>(props: {
  id: Field;
  useForm: UseForm<Field>;
  label: string;
  disabled?: boolean;
}): React.ReactElement {
  const { id, useForm, label, disabled } = props;

  return (
    <FormControlLabel
      control={
        <Checkbox
          disabled={disabled}
          checked={useForm.formStateHandle[0].values[id]}
          onChange={(event: ChangeEvent<HTMLInputElement>) => useForm.updateField(id)(event.target.checked)}
        />
      }
      label={label}
    />
  );
}

export function FormCheckboxList<Field extends string>(props: {
  id: Field;
  useForm: UseForm<Field>;
  sx?: SxProps<Theme>;
  items: CheckboxListItem[];
  label: string;
  onChange?: (values: string[]) => void;
}): React.ReactElement {
  const { id, useForm, items, label, onChange } = props;
  const sx: SxProps<Theme> = props.sx || {};
  const [state, setState] = useState(new Set(items.map((i) => i.value)));

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newState = new Set(state);

    if (event.target.checked) {
      newState.add(event.target.value);
    } else {
      newState.delete(event.target.value);
    }

    setState(newState);
  };

  useEffect(() => {
    useForm.updateField(id)(Array.from(state));
    if (onChange) onChange(Array.from(state));
  }, [state]);

  return (
    <FormControl sx={sx} component="fieldset" variant="standard">
      <FormLabel component="legend">{label}</FormLabel>
      <FormGroup>
        {items.map((item, index) => (
          <FormControlLabel
            key={index}
            control={
              <Checkbox checked={state.has(item.value)} onChange={handleChange} name={item.name} value={item.value} />
            }
            label={item.name}
          />
        ))}
      </FormGroup>
    </FormControl>
  );
}

export function FormTextField<Field extends string>(props: {
  className?: string;
  checkmark?: boolean;
  label: string;
  id: Field;
  fullWidth?: boolean;
  variant?: "standard" | "outlined" | "filled";
  size?: "small" | "medium";
  required?: boolean;
  type?: string;
  useForm: UseForm<Field>;
  disabled?: boolean;
  InputProps?: Partial<FilledInputProps>;
  sx?: SxProps<Theme>;
}): React.ReactElement {
  const { id, useForm } = props;
  const sx = props.sx || {};
  const InputProps = props.InputProps || {};
  return (
    <TextField
      sx={sx}
      id={id}
      label={props.label}
      name={id}
      size={props.size}
      variant={props.variant || "outlined"}
      required={props.required}
      className={props.className}
      fullWidth={props.fullWidth}
      type={props.type}
      error={useForm.hasError(id)}
      helperText={useForm.errorText(id)}
      onChange={useForm.handleChange}
      value={useForm.formStateHandle[0].values[id] || ""}
      disabled={props.disabled}
      InputProps={{
        ...InputProps,
        endAdornment: props.checkmark && (
          <InputAdornment position="end">{useForm.isGood(id) && <Checkmark />}</InputAdornment>
        ),
      }}
    />
  );
}
